import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'
import DataTable, { type Column } from '../components/DataTable'
import KpiGrid from '../components/KpiGrid'
import PreguntarAlSistema from '../components/PreguntarAlSistema'
import type {
  EstadoIncidencia, Incidencia, IncidenciasResponse, Severidad,
} from '../types/portal'

/**
 * "Incidencias" — qué falló, dónde y a quién le afectó.
 *
 * Junta en una lista lo que hoy hay que ir a buscar a cuatro consolas: alarmas
 * de CloudWatch, mensajes atascados en la DLQ, envíos que Meta rechazó y
 * documentos que se quedaron a mitad del pipeline. Más lo que reporta un asesor
 * desde el chat, que es la única fuente que ve lo que ningún monitoreo detecta.
 *
 * El fallo que originó esta pantalla lo reportó una asesora, no el monitoreo:
 * el bot le respondió a un cliente por encima de ella y nadie se enteró hasta
 * que ella lo dijo.
 *
 * La columna que importa es "a quién afecta". Una lista de recursos rotos es un
 * tablero de infraestructura; lo que hace falta para operar es saber que el
 * documento de Rosa Huamán lleva veinte minutos sin procesarse.
 */

const POLL_MS = 30_000

const TONO_SEVERIDAD: Record<Severidad, string> = {
  CRITICA: 'border-danger-border bg-danger-light text-danger',
  ALTA:    'border-danger-border bg-danger-light text-danger',
  MEDIA:   'border-warn-border bg-warn-light text-warn',
  BAJA:    'border-surface-border bg-surface text-ink-muted',
}

const TONO_ESTADO: Record<EstadoIncidencia, string> = {
  ABIERTA:  'border-warn-border bg-warn-light text-warn',
  EN_CURSO: 'border-info-border bg-info-light text-info',
  RESUELTA: 'border-brand-border bg-brand-light text-brand-hover',
}

const ETIQUETA_ESTADO: Record<EstadoIncidencia, string> = {
  ABIERTA: 'Abierta', EN_CURSO: 'En curso', RESUELTA: 'Resuelta',
}

function cuando(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const hoy = new Date().toDateString() === d.toDateString()
  const hora = d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
  return hoy ? `hoy ${hora}` : `${d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' })} ${hora}`
}

export default function Incidents() {
  const navigate = useNavigate()
  // `?focus=` llega desde el diagnóstico: resalta la incidencia de la que se
  // acaba de hablar. Sin esto el enlace deja al usuario mirando una tabla y
  // buscando a ojo cuál era.
  const [params] = useSearchParams()
  const foco = params.get('focus') ?? ''
  const [data, setData] = useState<IncidenciasResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [accion, setAccion] = useState<string | null>(null)
  const [verResueltas, setVerResueltas] = useState(false)

  const fetch = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true)
    try {
      const res = await apiClient.get<IncidenciasResponse>('/incidents')
      setData(res.data)
      setError(null)
    } catch (err) {
      setError(errorMessage(err, 'No se pudieron cargar las incidencias.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void fetch() }, [fetch])
  useEffect(() => {
    const id = setInterval(() => { void fetch(true) }, POLL_MS)
    return () => clearInterval(id)
  }, [fetch])

  const cambiarEstado = useCallback(async (inc: Incidencia, estado: EstadoIncidencia) => {
    setAccion(inc.id)
    try {
      await apiClient.put(`/incidents/${encodeURIComponent(inc.id)}`, { estado })
      void fetch(true)
    } catch (err) {
      setError(errorMessage(err, 'No se pudo actualizar la incidencia.'))
    } finally {
      setAccion(null)
    }
  }, [fetch])

  // Las resueltas se esconden por defecto: si se acumulan, la lista deja de
  // decir qué hay que atender ahora, que es para lo que se abre la pantalla.
  const visibles = useMemo(() => {
    const todas = data?.incidencias ?? []
    if (verResueltas) return todas
    // La enfocada se muestra aunque esté resuelta: si no, el enlace del
    // diagnóstico llevaría a una tabla donde no está lo que se acaba de nombrar.
    return todas.filter((i) => i.estado !== 'RESUELTA' || i.id === foco)
  }, [data, verResueltas, foco])

  const resueltas = (data?.incidencias ?? []).length - (data?.incidencias ?? [])
    .filter((i) => i.estado !== 'RESUELTA').length

  const columnas: Column<Incidencia>[] = [
    {
      key: 'severidad',
      header: 'Severidad',
      cell: (i) => (
        <span className={`inline-flex rounded-chip border px-2 py-0.5 text-pill font-bold ${TONO_SEVERIDAD[i.severidad]}`}>
          {i.severidad}
        </span>
      ),
    },
    {
      key: 'titulo',
      header: 'Qué pasó',
      cell: (i) => <span className="font-semibold">{i.titulo}</span>,
    },
    {
      key: 'afectado',
      header: 'Cliente / recurso',
      cell: (i) => <span>{i.afectado || '—'}</span>,
      sub: (i) => i.afectado_detalle,
    },
    {
      key: 'origen',
      header: 'Origen',
      cell: (i) => <span className="font-mono text-micro text-ink-muted">{i.origen}</span>,
    },
    {
      key: 'detectado',
      header: 'Detectado',
      cell: (i) => <span className="text-ink-muted">{cuando(i.detectado)}</span>,
    },
    {
      key: 'estado',
      header: 'Estado',
      cell: (i) => (
        <span className={`inline-flex rounded-chip border px-2 py-0.5 text-pill font-bold ${TONO_ESTADO[i.estado]}`}>
          {ETIQUETA_ESTADO[i.estado]}
        </span>
      ),
    },
    {
      key: 'accion',
      header: '',
      align: 'right',
      cell: (i) => (
        <div className="flex justify-end gap-1.5">
          {/* Solo hay traza si la incidencia cuelga de una conversación. Una
              alarma de throttling no tiene un caso que recorrer. */}
          {i.conversation_id && (
            <button
              className="btn-soft"
              onClick={(e) => { e.stopPropagation(); navigate(`/traces?conv=${i.conversation_id}`) }}
            >
              Ver traza
            </button>
          )}
          {i.estado !== 'RESUELTA' ? (
            <button
              className="btn-row disabled:opacity-50"
              disabled={accion === i.id}
              onClick={(e) => {
                e.stopPropagation()
                void cambiarEstado(i, i.estado === 'ABIERTA' ? 'EN_CURSO' : 'RESUELTA')
              }}
            >
              {accion === i.id ? '…' : i.estado === 'ABIERTA' ? 'Tomar' : 'Resolver'}
            </button>
          ) : (
            <button
              className="btn-row disabled:opacity-50"
              disabled={accion === i.id}
              onClick={(e) => { e.stopPropagation(); void cambiarEstado(i, 'ABIERTA') }}
            >
              Reabrir
            </button>
          )}
        </div>
      ),
    },
  ]

  const k = data?.kpis

  return (
    <div className="flex flex-col gap-5">
      <PreguntarAlSistema />

      <p className="rounded-card border border-info-border bg-info-light px-4 py-3 text-body text-info">
        Se abren solas. Además de las alarmas, la DLQ y los errores de Meta,
        detectan lo que no dispara ninguna alarma: clientes esperando a una
        persona, conversaciones que nadie tomó, ventanas de 24 h por vencer y el
        agente respondiendo donde ya atiende un asesor. Marcar resuelta no apaga
        la causa: dice que alguien se está ocupando.
      </p>

      <KpiGrid
        columns={4}
        kpis={[
          { label: 'Incidencias abiertas', value: k?.abiertas ?? 0,
            hint: k?.criticas_abiertas
              ? `${k.criticas_abiertas} crítica${k.criticas_abiertas === 1 ? '' : 's'} sin atender`
              : 'ninguna crítica',
            tone: k?.criticas_abiertas ? 'danger' : k?.abiertas ? 'warn' : 'default' },
          { label: 'Clientes esperando', value: k?.clientes_esperando ?? 0,
            hint: 'sin respuesta de una persona',
            tone: k?.clientes_esperando ? 'danger' : 'default' },
          { label: 'Docs no procesados', value: k?.docs_no_procesados ?? 0,
            hint: 'el cliente sigue esperando',
            tone: k?.docs_no_procesados ? 'danger' : 'default' },
          { label: 'Mensajes no entregados', value: k?.mensajes_no_entregados ?? 0,
            hint: 'últimas 24 h',
            tone: k?.mensajes_no_entregados ? 'warn' : 'default' },
        ]}
      />

      <DataTable
        title="Incidencias detectadas"
        subtitle="Automáticas desde CloudWatch y la DLQ, más los reportes de los asesores"
        columns={columnas}
        rows={visibles}
        rowKey={(i) => i.id}
        highlightKey={foco}
        loading={loading}
        error={error}
        onRetry={() => void fetch()}
        emptyTitle="Sin incidencias abiertas"
        emptyMessage="No hay alarmas activas, ni documentos trabados, ni mensajes sin entregar."
        action={
          <div className="flex items-center gap-2">
            {resueltas > 0 && (
              <button className="btn-ghost" onClick={() => setVerResueltas((v) => !v)}>
                {verResueltas ? 'Ocultar' : 'Ver'} {resueltas} resuelta{resueltas === 1 ? '' : 's'}
              </button>
            )}
            <button className="btn-ghost" onClick={() => void fetch()}>Actualizar</button>
          </div>
        }
      />
    </div>
  )
}
